import { cloneDeep } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Content, useContent } from 'src/contexts/ContentContext';
import { Switch } from 'src/core/interim/Switch';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Button, Stack } from 'src/core/ui';
import { useListingGroupSellerAccountSettings } from 'src/hooks/api/useListingGroupSellerAccountSettings';
import { useSyncCenterSettingsWithMutation } from 'src/hooks/api/useSyncCenterSettingsWithMutation';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature } from 'src/WebApiController';

import { SyncCenterFieldValues } from '../SyncCenter/SyncCenter.types';
import {
  setValueAsNumber,
  toSyncCenterFieldValues,
  toSyncCenterSettings,
} from '../SyncCenter/SyncCenter.utils';
import * as styles from './InventorySetting.css';

interface ListingGroupSettingsFormValues {
  defaultExcludeFullySoldInGroup: boolean;
  defaultCollapseListingGroups: boolean;
}

export function InventorySetting() {
  const hasAutoSplitDiscontinuousListingSettingFeature = useUserHasFeature(
    Feature.AutoSplitDiscontinuousListingSetting
  );
  const hasListingGroupAccountSettingsFeature = useUserHasFeature(
    Feature.ListingGroupAccountSettings
  );

  const greaterThanZeroRequired = useContent(ContentId.GreaterThanZeroRequired);

  // Listings settings (existing)
  const { query, mutation } = useSyncCenterSettingsWithMutation();

  const formDefaultValues = useMemo(() => {
    if (query.data) {
      return toSyncCenterFieldValues(cloneDeep(query.data));
    }
    return undefined;
  }, [query.data]);

  const { control, register, handleSubmit, reset, formState } =
    useForm<SyncCenterFieldValues>({
      defaultValues: formDefaultValues,
    });

  useEffect(() => {
    reset(formDefaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formDefaultValues]);

  // Listing Groups settings (new)
  const {
    data: listingGroupSettingsData,
    isLoading: isListingGroupSettingsLoading,
    mutation: listingGroupSettingsMutation,
  } = useListingGroupSellerAccountSettings();

  const listingGroupFormDefaultValues = useMemo(() => {
    return {
      defaultExcludeFullySoldInGroup:
        listingGroupSettingsData?.defaultExcludeFullySoldInGroup ?? false,
      defaultCollapseListingGroups:
        listingGroupSettingsData?.defaultCollapseListingGroups ?? false,
    };
  }, [listingGroupSettingsData]);

  const {
    control: listingGroupControl,
    handleSubmit: handleListingGroupSubmit,
    reset: resetListingGroup,
    formState: listingGroupFormState,
  } = useForm<ListingGroupSettingsFormValues>({
    defaultValues: listingGroupFormDefaultValues,
  });

  useEffect(() => {
    resetListingGroup(listingGroupFormDefaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingGroupFormDefaultValues]);

  const showListingsSection = true;

  if (query.isLoading || isListingGroupSettingsLoading) {
    return <PosSpinner />;
  }

  return (
    <div>
      <h5 className={styles.pageHeading}>
        <Content id={ContentId.Inventory} />
      </h5>

      {/* Listings Section */}
      {showListingsSection && (
        <div className={styles.sectionContainer}>
          <div className={styles.sectionName}>
            <Content id={ContentId.Listings} />
          </div>
          <div className={styles.sectionContent}>
            <Stack direction="column" gap="s">
              <div className={styles.label}>
                <Content id={ContentId.DefaultShownQuantity} />
              </div>
              <p className={styles.subtleText}>
                <Content
                  id={ContentId.DefaultMaxManualDisplayQuantityDescription}
                />
              </p>
              <PosFormField
                label={<></>}
                errors={
                  formState.errors.defaultManualMaxDisplayQuantity?.message
                }
                className={styles.textField}
              >
                <PosTextField
                  disabled={mutation.isPending}
                  type="number"
                  {...register('defaultManualMaxDisplayQuantity', {
                    setValueAs: setValueAsNumber,
                    validate: {
                      minValue: (value: number | null) => {
                        return value && value <= 0
                          ? greaterThanZeroRequired
                          : undefined;
                      },
                    },
                  })}
                />
              </PosFormField>
            </Stack>
            {hasAutoSplitDiscontinuousListingSettingFeature && (
              <Stack direction="column" gap="m">
                <Stack direction="row" gap="m" alignItems="center">
                  <div className={styles.label}>
                    <Content
                      id={ContentId.SplitListingWithDiscontinuousSeatsAutomatically}
                    />
                  </div>
                  <Controller
                    control={control}
                    name="enableListingSplitOnDiscontinuousOrdinal"
                    render={({ field: { value, onChange, ...field } }) => (
                      <Switch
                        disabled={mutation.isPending}
                        checked={Boolean(value)}
                        onChange={(e) => {
                          onChange(e.currentTarget.checked);
                        }}
                        {...field}
                      />
                    )}
                  />
                </Stack>
                <p className={styles.subtleText}>
                  <Content
                    id={
                      ContentId.SplitListingWithDiscontinuousSeatsAutomaticallyDescription
                    }
                  />
                </p>
              </Stack>
            )}

            <div className={styles.footer}>
              <Button
                disabled={mutation.isPending || !formState.isDirty}
                onClick={handleSubmit((data) => {
                  mutation.mutate(toSyncCenterSettings(data));
                })}
              >
                <Content id={ContentId.Save} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Listing Groups Section */}
      {hasListingGroupAccountSettingsFeature && (
        <div className={styles.sectionContainer}>
          <div className={styles.sectionName}>
            <Content id={ContentId.ListingGroups} />
          </div>
          <div className={styles.sectionContent}>
            <Stack direction="column" gap="m">
              <Stack direction="row" gap="m" alignItems="center">
                <div className={styles.label}>
                  <Content id={ContentId.ExcludeFullySoldListingsDefault} />
                </div>
                <Controller
                  control={listingGroupControl}
                  name="defaultExcludeFullySoldInGroup"
                  render={({ field: { value, onChange, ...field } }) => (
                    <Switch
                      disabled={listingGroupSettingsMutation.isPending}
                      checked={Boolean(value)}
                      onChange={(e) => {
                        onChange(e.currentTarget.checked);
                      }}
                      {...field}
                    />
                  )}
                />
              </Stack>
              <p className={styles.subtleText}>
                <Content
                  id={ContentId.ExcludeFullySoldListingsDefaultDescription}
                />
              </p>
            </Stack>

            <Stack direction="column" gap="m">
              <Stack direction="row" gap="m" alignItems="center">
                <div className={styles.label}>
                  <Content id={ContentId.CollapseGroupsByDefault} />
                </div>
                <Controller
                  control={listingGroupControl}
                  name="defaultCollapseListingGroups"
                  render={({ field: { value, onChange, ...field } }) => (
                    <Switch
                      disabled={listingGroupSettingsMutation.isPending}
                      checked={Boolean(value)}
                      onChange={(e) => {
                        onChange(e.currentTarget.checked);
                      }}
                      {...field}
                    />
                  )}
                />
              </Stack>
              <p className={styles.subtleText}>
                <Content id={ContentId.CollapseGroupsByDefaultDescription} />
              </p>
            </Stack>

            <div className={styles.footer}>
              <Button
                disabled={
                  listingGroupSettingsMutation.isPending ||
                  !listingGroupFormState.isDirty
                }
                onClick={handleListingGroupSubmit((data) => {
                  listingGroupSettingsMutation.mutate({
                    defaultExcludeFullySoldInGroup:
                      data.defaultExcludeFullySoldInGroup,
                    defaultCollapseListingGroups:
                      data.defaultCollapseListingGroups,
                  });
                })}
              >
                <Content id={ContentId.Save} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
